import { useIsMobile, useLazyRef } from '../shared/lib/hooks.ts'
import { cn } from '../shared/lib/utils.ts'
import { FormattedMessage } from 'react-intl'
import { getLessonIconByTypeOrIndex } from '../shared/data/programs.ts'
import { motion, AnimatePresence } from 'framer-motion'
import { Icon } from '../shared/ui/icon/icon.tsx'
import { useAppModule } from '../features/appContext.ts'
import {
  UserTestStoreContext,
  useUserTestStore,
} from '../features/tests/userTestStore.ts'
import { useEffect } from 'react'
import { UserTest, isTestAccessible } from '../shared/api/userTestsApi.ts'
import { useSnapshot } from 'valtio'
import { useNavigate } from 'react-router-dom'
import { urls } from '../shared/urls.ts'
import { Spinner } from '../shared/ui/spinner.tsx'

export function UserTestProvider({ children }: { children: React.ReactNode }) {
  const appModule = useAppModule()
  const store = useLazyRef(() => appModule.userTestStore()).current

  return (
    <UserTestStoreContext.Provider value={store}>
      {children}
    </UserTestStoreContext.Provider>
  )
}

export function UserTestModePage() {
  return (
    <UserTestProvider>
      <UserTestModePageContent />
    </UserTestProvider>
  )
}

function UserTestModePageContent() {
  const appModule = useAppModule()
  const store = useUserTestStore()
  const state = useSnapshot(store.state)
  const currentCourse = appModule.appStorage.get('currentCourse')
  const courseId = currentCourse?.id ?? undefined

  useEffect(() => {
    void store.getTodayTests(courseId)
  }, [store, courseId])

  if (state.loading)
    return (
      <div className="flex h-dvh w-full items-center justify-center">
        <Spinner />
      </div>
    )

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex w-full gap-18 overflow-hidden bg-white msm:pt-0 mxl:pt-16 xl:bg-bglight xl:px-40 xl:py-24"
    >
      <Units lessons={state.tests} title="Testing" loading={state.loading} />
    </motion.div>
  )
}

interface UnitProps {
  lessons: readonly UserTest[]
  title: string
  loading: boolean
}

function Units({ lessons, title, loading }: UnitProps) {
  return (
    <div className="h-fit mx-auto my-0 mb-100 flex w-full flex-col gap-16 bg-white p-16 xl:m-0  xl:w-[calc(100%-300px)] xl:rounded-24 xl:p-24 xl:shadow-drop   ">
      <UnitHeader title={title} id="user-testing-unit" />
      <div className="flex h-fit">
        <div
          className="relative  flex h-fit w-full flex-wrap gap-16 msm:flex-col xl:ml-20 xl:mt-18 xl:flex-col xl:flex-nowrap"
          id="user-testing-unit-lessons"
        >
          {lessons.length > 0 ? (
            lessons.map((lesson, index) => {
              return (
                <UserTestLessonCard
                  key={lesson.id}
                  lesson={lesson}
                  index={index}
                />
              )
            })
          ) : (
            <div className="text-14 font-normal text-black opacity-70">
              Нет доступных тестов на сегодня
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface UnitHeaderProps {
  title: string
  id: string
}

function UnitHeader({ title, id }: UnitHeaderProps) {
  const isMobile = useIsMobile()
  return (
    <div className="px-16 xl:px-0" id={`${id}-header`}>
      <div className="text-24 font-extrabold text-black xl:text-18 xl:leading-none">
        {isMobile ? (
          <FormattedMessage id="unit" />
        ) : (
          <FormattedMessage id="unitDesktop" values={{ title }} />
        )}
      </div>

      <div className="overflow-hidden text-ellipsis text-nowrap text-18 font-extrabold leading-none text-black xl:hidden">
        {title}
      </div>
    </div>
  )
}

interface UserTestLessonCardProps {
  lesson: UserTest
  index: number
}

const pseudoNumber =
  'relative xl:before:content-[attr(data-index)] xl:before:absolute xl:before:-left-[36px] xl:before:top-0 xl:before:flex xl:before:items-center xl:before:justify-center xl:before:size-[28px] xl:before:rounded-full xl:before:bg-gray-light xl:before:text-14 xl:before:font-extrabold xl:before:text-purple3'

function UserTestLessonCard({ lesson, index }: UserTestLessonCardProps) {
  const isDisabled =
    !lesson.enabled ||
    !isTestAccessible(lesson) ||
    lesson.allowed_attempts === 0
  const navigate = useNavigate()

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const accessPeriod =
    lesson.access_date_from && lesson.access_date_to
      ? `${formatDate(lesson.access_date_from)} - ${formatDate(
          lesson.access_date_to,
        )}`
      : null

  const handleClick = () => {
    if (!isDisabled) {
      navigate(urls.userTest(lesson.id.toString()), {
        state: {
          testType: lesson.type,
          testName: lesson.name,
          test: { ...lesson },
        },
      })
    }
  }

  return (
    <div
      className={cn(
        'relative flex w-full items-baseline rounded-18 bg-gray-light p-16',
        pseudoNumber,
        isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
      id={`user-test-${lesson.id}`}
      data-index={index + 1}
      aria-disabled={isDisabled}
      onClick={handleClick}
    >
      <div className="flex w-full items-center gap-12">
        <UserTestLessonIcon
          type={lesson.type}
          index={index}
          enabled={lesson.enabled}
        />
        <div className="text-start flex-1">
          <div className="text-14 font-extrabold text-black opacity-30">
            <FormattedMessage id="test.number" values={{ number: index + 1 }} />
          </div>

          <div
            className={cn(
              'mt-2 line-clamp-2 overflow-hidden text-ellipsis break-words text-18 font-bold leading-5 text-black',
              isDisabled && 'opacity-30',
            )}
          >
            {lesson.name}
          </div>

          {accessPeriod && (
            <div className="mt-4 text-14 font-normal text-black opacity-70">
              <FormattedMessage
                id="test.available"
                values={{ period: accessPeriod }}
              />
            </div>
          )}

          {lesson.allowed_attempts >= 0 && (
            <div className="mt-2 text-14 font-normal text-black opacity-70">
              <FormattedMessage
                id="test.attempts"
                values={{ count: lesson.allowed_attempts }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const UserTestLessonIcon = ({
  type,
  index,
  enabled,
}: {
  type: string
  index: number
  enabled: boolean
}) => {
  const isDisabled = !enabled
  const iconPath = getLessonIconByTypeOrIndex(type, index)

  return (
    <div className="relative">
      <div
        className={cn(
          'relative flex size-80 min-h-80 min-w-80 shrink-0 items-center justify-center rounded-full border-4 p-6',
          'border-lesson-icon-border',
          isDisabled && 'opacity-30',
        )}
      >
        <div
          className={cn(
            'flex size-full items-center justify-center overflow-hidden rounded-full',
            'bg-white',
          )}
        >
          <img className="w-2/3" src={iconPath} alt="" />
        </div>
      </div>
      <AnimatePresence mode="wait">
        {isDisabled && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          >
            <Icon
              iconName="lockClosed"
              className="size-32 text-neutralgray600"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
